import { Box, Card, CardContent, LinearProgress, Typography } from "@mui/material";
import { intersection, isEmpty, orderBy } from "lodash";
import { TripTagCategory } from "../../components/tagSelect/tags";
import { filterPackages } from "../../publicPackages/filterPackages";
import type { ListPublicPackagesResponseItem, PublicPackageResponse } from "../../publicPackages/publicPackagesApi";
import { usePublicPackages } from "../../publicPackages/usePublicPackages";
import { CardMediaImg } from "../CardImage";
import { CardLinkArea } from "../CardLinkArea";
import { packageImageSx } from "../packageMedia";
import { useAlternativePackages } from "./useAlternativePackages";

function useSimilarPackages(props: { forPackage: PublicPackageResponse; maxItems: number }) {
  const { packages, isLoading } = usePublicPackages();
  const groupTypeTags = props.forPackage.tags.filter((t) => t.category === TripTagCategory.GROUP_TYPE);
  const currentPackageId = props.forPackage.id;
  const matchingPackages = filterPackages(packages, groupTypeTags).filter((p) => p.id !== currentPackageId);
  const sorted = sortPackagesByMatchingTagsDesc(matchingPackages, groupTypeTags).slice(0, props.maxItems);
  return {
    isLoading,
    packages: sorted,
  };
}

interface Tag {
  readonly category: string;
  readonly value: string;
}
function sortPackagesByMatchingTagsDesc(matchingPackages: ListPublicPackagesResponseItem[], groupTypeTags: Tag[]) {
  return orderBy(matchingPackages, (p) => intersection(p.tags, groupTypeTags).length, "desc");
}

function SuggestedPackagesView(props: { packages: ListPublicPackagesResponseItem[]; children: string }) {
  return (
    <Box sx={{ mt: 8, pt: 5, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: "bold",
          mb: 4,
        }}
      >
        {props.children}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr", // Telefony: 1 kolumna
            sm: "repeat(2, 1fr)", // Tablety: 2 kolumny
            md: "repeat(4, 1fr)", // Desktop: 4 kolumny
          },
          gap: 3,
        }}
      >
        {props.packages.map((pkg, idx) => (
          <Card
            key={idx}
            elevation={0}
            sx={{
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <CardLinkArea
              to={`../${pkg.id.id}`}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                alignItems: "stretch",
                justifyContent: "flex-start",
              }}
            >
              <CardContent sx={{ p: 2, pb: 1, flexGrow: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  {pkg.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                    mb: 1,
                  }}
                >
                  {pkg.packageName}
                </Typography>
              </CardContent>
              <CardMediaImg
                imageUrl={pkg.images[0]?.thumbnail?.url}
                sx={{ ...packageImageSx, borderRadius: "0 0 4px 4px", mt: "auto" }}
              />
            </CardLinkArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

function SimilarPackages(props: { forPackage: PublicPackageResponse; maxItems: number }) {
  const similar = useSimilarPackages(props);
  if (similar.isLoading) {
    return <LinearProgress />;
  }
  if (isEmpty(similar.packages)) {
    return <></>;
  }
  return (
    <SuggestedPackagesView packages={similar.packages}>
      Sprawdź inne propozycje pakietów dla Twoich klientów
    </SuggestedPackagesView>
  );
}

export function SuggestedPackages(props: { forPackage: PublicPackageResponse; maxItems: number }) {
  const alternative = useAlternativePackages(props);
  if (alternative.isLoading) {
    return <LinearProgress />;
  }
  if (isEmpty(alternative.packages)) {
    return <SimilarPackages {...props} />;
  }
  return <SuggestedPackagesView packages={alternative.packages}>Pakiety alternatywne</SuggestedPackagesView>;
}
